import {defineStore} from 'pinia'
import {computed, inject, ref} from "vue";
import i18n from "@/plugins/i18n";
import axios from "axios";
import { useSnackbarStore } from "@es-bill/es-bill-common-components";
import { BACKEND_BASEURL } from "@/config/config";

// TODO: Remove Promises in async function: redundant and may cause unwanted behavior
// TODO DRY: Move to common components on TS conversion - this code is double to reloaded
export const useApplicantStore
  = defineStore('applicantStore', () => {
  // injected from main.js
  const $log = inject('log');

  // provided by configProvider

  const snackbarStore = useSnackbarStore();

  ////////////////////////////////////////////////////////////////////////
  const applicants = ref([]);
  const applicantsLoading = ref(false);
  const allApplicants =
    computed(() => applicants.value);
  const allApplicantsLoading =
    computed(() => applicantsLoading.value);

  function postProcessApplicants(applicants) {
    let al = [];

    // convert types
    for (const a of applicants) {

      // "migration" issues for old (first) applications
      const notesCommunity = a.notesCommunity || ""; // to be writeable if unset
      const applicationStatus = a.applicationStatus || "MEMBER_APPLIED"; // migration
      if(!a.applicant.contactData)
        a.applicant.contactData = {}
      const nameFrist = a.applicant.contactData.nameFirst || "";
      const nameLast = a.applicant.contactData.nameLast || "";

      let mps = [];
      let index = 0;
      for(const mp of a.meteringPointsAssociated){
        // Only initialize accountingReference if it doesn't exist, preserve existing structure
        const accountingReference = mp.accountingReference || {};

        const useOfflineProcess = mp.useOfflineProcess || false; // to be writeable if unset

        mps.push({
          ...mp,
          meteringPointIndex: index++,
          accountingReference,
          useOfflineProcess: useOfflineProcess,
        })
      }

      let aNew = {
        ...a,
        // reference
        submissionId: a._id,

        // overwrite
        notesCommunity: notesCommunity,
        applicationStatus: applicationStatus,
        submissionDateTime: new Date(a.submissionDateTime),
        meteringPointsAssociated: mps,
      };

      aNew.applicant.contactData.nameFirst = nameFrist;
      aNew.applicant.contactData.nameLast = nameLast;

      al.push(aNew)
    }

    return al;
  }

  async function fetchApplications() {
    applicantsLoading.value = true;
    applicants.value = [];
    try {
      const response = await axios.get(BACKEND_BASEURL + 'metadata/applicants');
      $log.debug(response.data);
      applicants.value = postProcessApplicants(response.data);
      applicantsLoading.value = false;
      return applicants.value;
    } catch (error) {
      handleApiError(error, 'applicantMessages.dataChangeError', 'applicantMessages.dataChangeErrorDescription');
      return null;
    }
  }

  /**
   * Fetches the precondition status for a specific application
   * @param {string} applicantId - The unique identifier of the applicant
   * @param {string} ecn - The energy community number (ECN) identifier
   * @returns {Promise<string|null>} The precondition status data from the API, or null if parameters are invalid or request fails
   */
  async function fetchApplicationsPreconditionStatus (applicantId, ecn) {
    if (!applicantId || !applicantId.length || !ecn || !ecn.length)
      return null;

    try {
      const response = await axios.get(BACKEND_BASEURL + 'applications/' + ecn + '/' + applicantId + "/preconditionStatus");
      $log.debug(response.status);
      return response.data;
    } catch (error) {
      handleApiError(error, 'applicantMessages.preconditionFailed', 'applicantMessages.preconditionFetchFailed');
      return i18n.global.t('applicantMessages.preconditionFetchFailed');
    }
  }

  /**
   * Delete an application
   * @param {string} applicationId - the id of the application
   * @param {string} ecn - the ecn of the community
   * @returns {Promise<boolean>} true if successful, false otherwise
   */
  async function deleteApplication(applicationId, ecn) {
    try {
      const response = await axios.delete(BACKEND_BASEURL + 'applications/' + ecn + '/' + applicationId);
      $log.debug(response.status);
      $log.debug("removing from local store");

      const indexApplication = applicants.value.findIndex(
        (a)=> a.ecn === ecn && a.submissionId === applicationId);
      if(indexApplication !== -1) // "should" never fail
        applicants.value.splice(indexApplication,1)

      snackbarStore.showSnackbar(i18n.global.t('applicantMessages.deletedSuccess'), i18n.global.t('applicantMessages.deletedDescription'), "Success", 5000, []);
      return true;
    } catch (error) {
      $log.warn(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        $log.warn("Server responded with error:", error.response.status, error.response.data);
        if (error.response.status === 404) {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.deletedError'), i18n.global.t('applicantNotFound'), "Error", 5000, []);
        } else {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.deletedError'), i18n.global.t('deleteError'), "Error", 5000, []);
        }
      } else if (error.request) {
        // The request was made but no response was received
        $log.warn("No response received:", error.request);
        snackbarStore.showSnackbar(i18n.global.t('connectionProblem'), "", "Warning", 5000, []);
      } else {
        // Something happened in setting up the request that triggered an Error
        $log.warn("Error setting up request:", error.message);
        snackbarStore.showSnackbar(i18n.global.t('applicantMessages.deletedError'), i18n.global.t('deleteError'), "Error", 5000, []);
      }
      return false;
    }
  }

  /**
   * Updates an application in the backend and local store
   * @param {Object} applicationNew - The updated application data
   * @returns {Promise<Object|null>} The updated applicant object or null if the operation failed
   */
  async function updateApplication(applicationNew) {
    // applicantNew is still references by submissionId
    if(!applicationNew || !applicants.value.find(a => a.submissionId === applicationNew.submissionId))
      return null; // direct fail if nothing is given or we cannot find object

    try {
      const response = await axios.put(
        BACKEND_BASEURL + 'applications/' + applicationNew.ecn + '/' + applicationNew.submissionId,
        applicationNew
      );

      $log.debug(response.status, response.data);
      $log.debug("updating in local store");

      const indexApplication = applicants.value.findIndex(
        (a) => a.ecn === applicationNew.ecn && a.submissionId === applicationNew.submissionId
      );
      const updatedApplicant = postProcessApplicants([response.data])[0];

      if(indexApplication !== -1) { // "should" never fail
        applicants.value.splice(indexApplication, 1, updatedApplicant);
      }

      return updatedApplicant;
    } catch (error) {
      handleApiError(error, 'applicantMessages.dataChangeError', 'applicantMessages.dataChangeErrorDescription');
      return null;
    }
  }

  /**
   * Declines an application and optionally archives it
   * @param {string} applicationId - The application ID (submissionId)
   * @param {string} ecn - The ECN of the community
   * @param {boolean} isArchived - Whether to archive the application
   * @returns {Promise<Object|null>} The updated applicant object or null if the operation failed
   */
  async function declineApplication(applicationId, ecn, isArchived = false) {
    if (!applicationId || !ecn) {
      return null;
    }

    try {
      const response = await axios.post(
        BACKEND_BASEURL + 'applications/' + ecn + '/' + applicationId + '/decline',
        {
          isArchived
        }
      );

      $log.debug(response.status, response.data);
      $log.debug("updating in local store");

      const indexApplication = applicants.value.findIndex(
        (a) => a.ecn === ecn && a.submissionId === applicationId
      );
      const updatedApplicant = postProcessApplicants([response.data])[0];

      if(indexApplication !== -1) { // "should" never fail
        applicants.value.splice(indexApplication, 1, updatedApplicant);
      }

      return updatedApplicant;
    } catch (error) {
      handleApiError(error, 'applicantMessages.dataChangeError', 'applicantMessages.dataChangeErrorDescription');
      return null;
    }
  }

  /**
   * Handle API errors with appropriate translations and notifications
   * @param {Error} error - The error object from axios
   * @param {string} defaultTitleKey - The i18n key for the error title
   * @param {string} defaultMessageKey - The i18n key for the default error message
   */
  function handleApiError(error, defaultTitleKey, defaultMessageKey) {
    $log.warn(error);

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const status = error.response.status;
      $log.warn("Server responded with error:", status, error.response.data);

      const errorStatusToTranslationKey = {
        400: 'applicantMessages.errorBadRequest',
        404: 'applicantMessages.errorNotFound',
        405: 'applicantMessages.errorMethodNotAllowed',
        406: 'applicantMessages.errorNotAcceptable',
        409: 'applicantMessages.errorConflict',
        500: 'applicantMessages.errorInternalServer'
      };

      const errorTitle = i18n.global.t(defaultTitleKey);
      let errorMessage = '';

      if (errorStatusToTranslationKey[status]) {
        // Use the specific error message for this status code
        errorMessage = i18n.global.t(errorStatusToTranslationKey[status]);
      } else {
        // Default error message for other status codes
        errorMessage = i18n.global.t(defaultMessageKey);
      }

      // Show the appropriate snackbar notification
      snackbarStore.showSnackbar(errorTitle, errorMessage, "Error", 5000, []);
    } else if (error.request) {
      // The request was made but no response was received
      $log.warn("No response received:", error.request);
      snackbarStore.showSnackbar(i18n.global.t('connectionProblem'), "", "Warning", 5000, []);
    } else {
      // Something happened in setting up the request that triggered an Error
      $log.warn("Error setting up request:", error.message);
      snackbarStore.showSnackbar(i18n.global.t(defaultTitleKey), i18n.global.t(defaultMessageKey), "Error", 5000, []);
    }
  }

  async function acceptApplicant(application, acceptanceData) {
    if(!application || !acceptanceData)
      return false;
    try {
      const response = await axios.post(
        BACKEND_BASEURL + 'applications/' + application.ecn + '/' + application.submissionId + "/acceptApplicant",
        acceptanceData
      );

      $log.debug(response.status, response.data);

      const indexApplication = applicants.value.findIndex(
        (a)=> a.ecn === application.ecn && a.submissionId === application.submissionId);
      if(indexApplication !== -1) // "should" never fail
        applicants.value.splice(indexApplication,1,postProcessApplicants([response.data])[0])

      snackbarStore.showSnackbar(i18n.global.t('applicantMessages.acceptedSuccess'), i18n.global.t('applicantMessages.acceptedDescription'), "Success", 5000, []);
      return true;
    } catch (error) {
      $log.warn(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        $log.warn("Server responded with error:", error.response.status, error.response.data);
        if (error.response.status === 404) {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.acceptedError'), i18n.global.t('applicantNotFound'), "Error", 5000, []);
        } else if (error.response.status === 400) {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.acceptedError'), i18n.global.t('applyError'), "Error", 5000, []);
        } else {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.acceptedError'), i18n.global.t('applyError'), "Error", 5000, []);
        }
      } else if (error.request) {
        // The request was made but no response was received
        $log.warn("No response received:", error.request);
        snackbarStore.showSnackbar(i18n.global.t('connectionProblem'), "", "Warning", 5000, []);
      } else {
        // Something happened in setting up the request that triggered an Error
        $log.warn("Error setting up request:", error.message);
        snackbarStore.showSnackbar(i18n.global.t('applicantMessages.acceptedError'), i18n.global.t('applyError'), "Error", 5000, []);
      }
      return false;
    }
  }

  async function createDocument(documentName, application, download, sendToMember) {
    if(!application || download === undefined || sendToMember === undefined)
      return {success: false, documentName: documentName, documentData: null};

    try {
      const response = await axios.post(BACKEND_BASEURL + 'applications/' + application.ecn + '/' +
        application.submissionId + "/generateDocument/energielieferabgabevertrag", null,
        { params: {
          sendToUser: sendToMember,
            download: download
        },
          responseType: 'blob', /// when getting other than plaintext!
        }

      );

      $log.debug(response.status);

      if(response.status === 204) {
        $log.debug("successful");
        return {success: true, documentName: documentName, documentData: null};
      }
      else if(response.status === 200) {
        $log.debug("successful and got contract");
        //$log.debug(response.headers);

        const blobData = new Blob([response.data],
          {type: 'application/pdf'});
        return {success: true, documentName: documentName, documentData: blobData};
      }
      else
        throw new Error("unknown response code");

    } catch (error) {
      $log.warn(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        $log.warn("Server responded with error:", error.response.status, error.response.data);
        if (error.response.status === 404) { // TODO more spefic error handling
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.documentGenerationFailed'), i18n.global.t('applicantNotFound'), "Error", 5000, []);
        } else {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.documentGenerationFailed'), i18n.global.t('applicantMessages.documentGenerationFailedText'), "Error", 5000, []);
        }
      } else if (error.request) {
        // The request was made but no response was received
        $log.warn("No response received:", error.request);
        snackbarStore.showSnackbar(i18n.global.t('connectionProblem'), "", "Warning", 5000, []);
      } else {
        // Something happened in setting up the request that triggered an Error
        $log.warn("Error setting up request:", error.message);
        snackbarStore.showSnackbar(i18n.global.t('applicantMessages.documentGenerationFailed'), i18n.global.t('applicantMessages.documentGenerationFailedText'), "Error", 5000, []);
      }      return {success: false, documentName: documentName, documentData: null};
    }
  }

  /**
   * Moves an applicant to the members list after contracts have been completed.
   *
   * Sends a POST request to the backend to move the applicant to member status.
   * Updates the local store with the new member data if the operation is successful.
   * Displays appropriate error messages in case of failure.
   *
   * @param {Object} application - The application object containing ecn and submissionId.
   * @param {Object} completeData - The data required to complete the process.
   * @returns {Promise<Object|Null>} - Returns the new member object if the operation is successful, otherwise null.
   */
  async function finishApplicantAfterContractsDone(application, completeData) {
    if(!application || !completeData)
      return null;

    try {
      const response = await axios.post(BACKEND_BASEURL + 'applications/' + application.ecn + '/' +
        application.submissionId + "/moveApplicantToMembers",
        completeData);

      $log.debug(response.status, response.data);
      $log.debug("updating in local store");

      const indexApplication = applicants.value.findIndex(
        (a)=> a.ecn === application.ecn && a.submissionId === application.submissionId);
      const applicationNew = postProcessApplicants([response.data])[0];
      if(indexApplication !== -1) // "should" never fail
        applicants.value.splice(indexApplication,1, applicationNew)

      return applicationNew;
    } catch (error) {
      $log.warn(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        $log.warn("Server responded with error:", error.response.status, error.response.data);
        if (error.response.status === 400) {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.memberCreationFailed'), i18n.global.t('applicantMessages.memberCreationFailedText'), "Error", 5000, []);
        } else if (error.response.status === 405) {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.preconditionFailed'), i18n.global.t('applicantMessages.preconditionFailedText'), "Error", 5000, []);
        } else if (error.response.status === 404) {
          snackbarStore.showSnackbar(i18n.global.t('communityNotFound'), '', "Error", 5000, []);
        } else if (error.response.status === 406) {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.notAcceptable'), i18n.global.t('applicantMessages.notAcceptableText'), "Error", 5000, []);
        } else {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.memberCreationFailed'), i18n.global.t('applicantMessages.memberCreationFailedText'), "Error", 5000, []);
        }
      } else if (error.request) {
        // The request was made but no response was received
        $log.warn("No response received:", error.request);
        snackbarStore.showSnackbar(i18n.global.t('connectionProblem'), "", "Warning", 5000, []);
      } else {
        // Something happened in setting up the request that triggered an Error
        $log.warn("Error setting up request:", error.message);
        snackbarStore.showSnackbar(i18n.global.t('applicantMessages.memberCreationFailed'), i18n.global.t('applicantMessages.memberCreationFailedText'), "Error", 5000, []);
      }
      return null;
    }
  }

  /**
   * Retrieves an application by id
   * @param applicantId {string} the _id of the application (legacy 'applicant')
   * @returns {Object} the application
   */
  function getApplicationById(applicantId) { // TODO replace all manual searches with this
    return applicants.value.find(a => a.submissionId === applicantId  || a._id === applicantId);
  }

  /**
   * Get all applications for a specific ecn
   * @param ecn {string} - the ecn of the community
   * @returns {[]} - array of applications
   */
  function getApplicationByEcn(ecn) {
    return applicants.value.filter(a => a.ecn === ecn);
  }

  /**
   * Persist it to the backend and store result in store (reactive pattern)
   * @param {MemberApplicantModel} memberApplication - the new applicant object stored
   * @returns {Promise<MemberApplicantModel>} - the new applicant object
   */
  async function createNewApplication(memberApplication) {
    if (!memberApplication)
      return null;

    try {
      const response = await axios.post(BACKEND_BASEURL + 'applications/' + memberApplication.ecn +
        '/createNewApplicantForContractChange/' + memberApplication.applicant.clientCode, memberApplication);
      $log.debug(response.status, response.data);
      $log.debug("updating in local store");

      const postProcessedApplicant = postProcessApplicants([response.data])[0];
      applicants.value.push(postProcessedApplicant);
      return postProcessedApplicant;

    } catch (error) {
      $log.error(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        $log.warn("Error response:", error.response.status, error.response.data);
        if (error.response.status === 409) {
          // Let the error be handled by the composable
          error._shouldRedirectToViewApplicants = true;
          error._applicantId = error.response.data;
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.applicationAlreadyExists'), i18n.global.t('applicantMessages.applicationAlreadyExistsDescription'), "Error", 5000, []);
          throw error;
        } else if (error.response.status === 404) { // Todo write specific error message
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.applicationCreationFailed'), i18n.global.t('applicantMessages.applicationCreationFailedText'), "Error", 5000, []);
        } else {
          snackbarStore.showSnackbar(i18n.global.t('applicantMessages.applicationCreationFailed'), i18n.global.t('applicationMessages.applicationCreationFailedText'), "Error", 5000, []);
        }
      } else if (error.request) {
        // The request was made but no response was received
        $log.warn("No response received:", error.request);
        snackbarStore.showSnackbar(i18n.global.t('connectionProblem'), "", "Warning", 5000, []);
      } else {
        // Something happened in setting up the request that triggered an Error
        $log.warn("Error setting up request:", error.message);
        snackbarStore.showSnackbar(i18n.global.t('applicantMessages.applicationCreationFailed'), i18n.global.t('applicantMessages.applicationCreationFailedText'), "Error", 5000, []);
      }
      return null;
    }
  }

  return {
    allApplicants, // TODO should noe be exported, but with getter
    allApplicantsLoading,
    createNewApplication,
    getApplicationById,
    getApplicationByEcn,
    fetchApplications,
    fetchApplicationsPreconditionStatus,
    deleteApplication,
    updateApplication,
    declineApplication,
    acceptApplicant,
    createDocument,
    finishApplicantAfterContractsDone,
  }
});
